import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { analytics } from '../lib/analytics';
import { motion } from 'framer-motion';
import { Star, Car, Shield, Clock, CreditCard, MapPin, CalendarBlank, CaretRight, CaretLeft, Users, CheckCircle } from '@phosphor-icons/react';

const HERO_BG = "./img/herodestidriving.webp";

function HeroSection() {
  const navigate = useNavigate();
  
  const handleReserveClick = () => {
    analytics.trackEvent('hero_cta_click');
    document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const handleServicesClick = () => {
    analytics.trackEvent('hero_services_click');
    navigate('/service-vtc');
  };
  
  return (
    <section className="relative h-screen flex items-center" data-testid="hero-section">
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="VTC Premium" className="w-full h-full object-cover" />
        <div className="hero-gradient absolute inset-0" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
          <p className="text-xs uppercase tracking-[0.3em] text-desti-gold mb-4">VTC Premium Paris</p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.1] max-w-3xl">
            Votre chauffeur <br /><span className="gold-gradient-text">d'exception</span>
          </h1>
          <p className="mt-6 text-lg text-white/60 max-w-xl font-body font-light">
            Confort, sécurité et ponctualité pour tous vos déplacements à Paris et en Ile-de-France. Disponible 24h/24.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <button
              onClick={handleReserveClick}
              className="bg-desti-gold text-desti-black font-medium text-sm tracking-wide px-8 py-4 hover:bg-desti-gold-hover transition-colors"
              data-testid="hero-cta-btn"
            >
              Réserver maintenant
            </button>
            <button
              onClick={handleServicesClick}
              className="border border-white/20 text-white text-sm tracking-wide px-8 py-4 hover:bg-white/5 hover:border-desti-gold transition-colors"
              data-testid="hero-services-btn"
            >
              Nos services
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function BookingStepper() {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    pickupDate: '', pickupTime: '', pickupAddress: '', dropoffAddress: '',
    pickupLat: 0, pickupLng: 0, dropoffLat: 0, dropoffLng: 0,
    tripType: 'ONE_WAY', waitingTime: 0, distanceKm: 0, durationMin: 0,
    vehicleType: '', contactName: '', contactEmail: '', contactPhone: '',
  });
  const [timeSlots, setTimeSlots] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [priceInfo, setPriceInfo] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const pickupRef = useRef(null);
  const dropoffRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const bookingStartedTracked = useRef(false);

  // Tracker que l'utilisateur a commencé une réservation
  useEffect(() => {
    if (!bookingStartedTracked.current) {
      bookingStartedTracked.current = true;
      analytics.trackEvent('booking_started');
    }
  }, []);

  useEffect(() => {
    api.get('/vehicles')
      .then(r => setVehicles(Array.isArray(r.data) ? r.data : []))
      .catch(() => setVehicles([]));
  }, []);

  useEffect(() => {
    if (formData.pickupDate) {
      api.get(`/timeslots?date=${formData.pickupDate}`)
        .then(r => setTimeSlots(r.data.slots || []))
        .catch(() => setTimeSlots([]));
    }
  }, [formData.pickupDate]);

  const initAutocomplete = useCallback(() => {
    if (!window.google?.maps?.places) return;
    const options = { componentRestrictions: { country: 'fr' } };
    if (pickupRef.current && !pickupRef.current._autocomplete) {
      const ac = new window.google.maps.places.Autocomplete(pickupRef.current, options);
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (place.geometry) {
          setFormData(p => ({ ...p, pickupAddress: place.formatted_address, pickupLat: place.geometry.location.lat(), pickupLng: place.geometry.location.lng() }));
        }
      });
      pickupRef.current._autocomplete = ac;
    }
    if (dropoffRef.current && !dropoffRef.current._autocomplete) {
      const ac = new window.google.maps.places.Autocomplete(dropoffRef.current, options);
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (place.geometry) {
          setFormData(p => ({ ...p, dropoffAddress: place.formatted_address, dropoffLat: place.geometry.location.lat(), dropoffLng: place.geometry.location.lng() }));
        }
      });
      dropoffRef.current._autocomplete = ac;
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (window.google?.maps?.places) {
        initAutocomplete();
        clearInterval(interval);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [initAutocomplete]);

  useEffect(() => {
    if (step === 1 && mapRef.current && window.google?.maps && !mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 48.8566, lng: 2.3522 },
        zoom: 11,
        styles: [
          { elementType: 'geometry', stylers: [{ color: '#1a1a1a' }] },
          { elementType: 'labels.text.stroke', stylers: [{ color: '#0d0d0d' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
          { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2a2720' }] },
          { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e1626' }] },
        ],
        disableDefaultUI: true,
        zoomControl: true,
      });
      directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
        map: mapInstanceRef.current,
        polylineOptions: { strokeColor: '#c9a84c', strokeWeight: 4 },
        suppressMarkers: false,
      });
    }
  }, [step]);

  const calculateRoute = async () => {
    if (!formData.pickupLat || !formData.dropoffLat) return;
    setLoading(true);
    try {
      const { data } = await api.post('/maps/distance', {
        originLat: formData.pickupLat, originLng: formData.pickupLng,
        destLat: formData.dropoffLat, destLng: formData.dropoffLng,
      });
      setRouteInfo(data);
      setFormData(p => ({ ...p, distanceKm: data.distanceKm, durationMin: data.durationMin }));
      if (window.google?.maps && directionsRendererRef.current) {
        const ds = new window.google.maps.DirectionsService();
        ds.route({
          origin: { lat: formData.pickupLat, lng: formData.pickupLng },
          destination: { lat: formData.dropoffLat, lng: formData.dropoffLng },
          travelMode: window.google.maps.TravelMode.DRIVING,
        }, (result, status) => {
          if (status === 'OK') directionsRendererRef.current.setDirections(result);
        });
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    if (formData.pickupLat && formData.dropoffLat) calculateRoute();
  }, [formData.pickupLat, formData.dropoffLat]);

  const selectVehicle = async (type) => {
    setFormData(p => ({ ...p, vehicleType: type }));
    
    // Tracking : sélection du véhicule
    analytics.trackEvent('vehicle_selected', { vehicleType: type });
    
    try {
      const { data } = await api.post('/bookings/calculate-price', {
        distanceKm: formData.distanceKm, vehicleType: type,
        tripType: formData.tripType, waitingTime: formData.waitingTime,
      });
      setPriceInfo(data);
    } catch (e) { console.error(e); }
  };

  const handlePayment = async () => {
    setLoading(true);
    
    // Tracking : début du checkout
    analytics.trackEvent('checkout_start', {
      vehicleType: formData.vehicleType,
      tripType: formData.tripType,
      distanceKm: formData.distanceKm,
      isLoggedIn: !!user
    });
    
    try {
      const bookingPayload = {
        ...formData,
        contactName: formData.contactName || (user ? `${user.firstName} ${user.lastName}` : ''),
        contactEmail: formData.contactEmail || (user?.email || ''),
        contactPhone: formData.contactPhone || (user?.phone || ''),
      };
      const { data: booking } = await api.post('/bookings', bookingPayload);
      const originUrl = window.location.origin;
      const { data: checkout } = await api.post('/payments/checkout', { bookingId: booking.id, originUrl });
      
      // Tracking : redirection Stripe
      analytics.trackEvent('stripe_redirect', { bookingId: booking.id });
      
      if (checkout.url) window.location.href = checkout.url;
    } catch (e) {
      console.error('Payment error:', e);
      // Tracking : erreur de paiement
      analytics.trackEvent('checkout_error', { error: e.message });
    }
    setLoading(false);
  };

  const handleStepChange = (newStep) => {
    // Tracker le changement d'étape
    if (newStep === 2 && step === 1) {
      analytics.trackEvent('step_completed', { step: 1, name: 'itineraire' });
    }
    if (newStep === 3 && step === 2) {
      analytics.trackEvent('step_completed', { step: 2, name: 'vehicule', vehicleType: formData.vehicleType });
    }
    setStep(newStep);
  };

  const today = new Date().toISOString().split('T')[0];
  const STEPS = ['Itinéraire', 'Véhicule', 'Paiement', 'Confirmation'];

  return (
    <section id="booking" className="py-24 md:py-32" data-testid="booking-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-desti-gold mb-3">Réservation</p>
          <h2 className="font-heading text-3xl sm:text-4xl text-white">Réserver votre course</h2>
        </div>

        <div className="flex items-center justify-center gap-2 mb-12">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                step > i + 1 ? 'bg-desti-gold text-desti-black' : step === i + 1 ? 'bg-desti-gold text-desti-black' : 'bg-white/10 text-white/40'
              }`} data-testid={`step-indicator-${i + 1}`}>
                {step > i + 1 ? <CheckCircle size={16} weight="bold" /> : i + 1}
              </div>
              <span className={`hidden sm:inline text-xs tracking-wide ${step === i + 1 ? 'text-desti-gold' : 'text-white/40'}`}>{s}</span>
              {i < 3 && <div className={`w-8 sm:w-16 h-px ${step > i + 1 ? 'bg-desti-gold' : 'bg-white/10'}`} />}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-desti-surface border border-white/5 p-6 md:p-8" data-testid="booking-form">
            {step === 1 && (
              <div className="space-y-5">
                <h3 className="font-heading text-xl text-white mb-6">Calcul d'itinéraire</h3>
                
                {/* Date et Heure - Version corrigée pour mobile */}
                <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Date</label>
                    <input 
                      type="date" 
                      min={today} 
                      value={formData.pickupDate}
                      onChange={e => setFormData(p => ({ ...p, pickupDate: e.target.value }))}
                      className="w-full bg-desti-black border-b border-white/20 px-3 py-3 text-white text-sm focus:outline-none focus:border-desti-gold transition-colors [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert"
                      data-testid="pickup-date-input" 
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Heure</label>
                    <select 
                      value={formData.pickupTime}
                      onChange={e => setFormData(p => ({ ...p, pickupTime: e.target.value }))}
                      className="w-full bg-desti-black border-b border-white/20 px-3 py-3 text-white text-sm focus:outline-none focus:border-desti-gold transition-colors appearance-none"
                      data-testid="pickup-time-select"
                    >
                      <option value="">Sélectionnez une heure</option>
                      {timeSlots?.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Départ</label>
                  <input ref={pickupRef} type="text" placeholder="Lieu de prise en charge"
                    className="w-full bg-desti-black border-b border-white/20 px-3 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-desti-gold transition-colors"
                    data-testid="pickup-address-input" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Destination</label>
                  <input ref={dropoffRef} type="text" placeholder="Destination"
                    className="w-full bg-desti-black border-b border-white/20 px-3 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-desti-gold transition-colors"
                    data-testid="dropoff-address-input" />
                </div>
                
                {/* Type et Attente - Version corrigée pour mobile */}
                <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Type</label>
                    <select 
                      value={formData.tripType}
                      onChange={e => setFormData(p => ({ ...p, tripType: e.target.value }))}
                      className="w-full bg-desti-black border-b border-white/20 px-3 py-3 text-white text-sm focus:outline-none focus:border-desti-gold transition-colors appearance-none"
                      data-testid="trip-type-select"
                    >
                      <option value="ONE_WAY">Aller simple</option>
                      <option value="ROUND_TRIP">Aller-retour</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Attente</label>
                    <select 
                      value={formData.waitingTime}
                      onChange={e => setFormData(p => ({ ...p, waitingTime: parseInt(e.target.value) }))}
                      className="w-full bg-desti-black border-b border-white/20 px-3 py-3 text-white text-sm focus:outline-none focus:border-desti-gold transition-colors appearance-none"
                      data-testid="waiting-time-select"
                    >
                      <option value={0}>Pas d'attente</option>
                      <option value={30}>30 min (+15€)</option>
                      <option value={60}>1h (+30€)</option>
                    </select>
                  </div>
                </div>

                {routeInfo && (
                  <div className="flex items-center gap-6 bg-desti-black/50 p-4 border border-desti-gold/20" data-testid="route-info">
                    <div><span className="text-xs text-desti-gold uppercase">Distance</span><p className="text-white font-medium">{routeInfo.distanceText}</p></div>
                    <div className="w-px h-8 bg-white/10" />
                    <div><span className="text-xs text-desti-gold uppercase">Durée</span><p className="text-white font-medium">{routeInfo.durationText}</p></div>
                  </div>
                )}
                <button onClick={() => handleStepChange(2)}
                  disabled={!formData.pickupDate || !formData.pickupTime || !formData.pickupLat || !formData.dropoffLat}
                  className="w-full bg-desti-gold text-desti-black font-medium py-4 tracking-wide hover:bg-desti-gold-hover transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="step1-next-btn">
                  Voir les véhicules <CaretRight size={16} weight="bold" />
                </button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <h3 className="font-heading text-xl text-white mb-6">Choisissez votre véhicule</h3>
                <div className="space-y-4">
                  {vehicles?.map(v => (
                    <div key={v.id}
                      onClick={() => selectVehicle(v.type)}
                      className={`p-4 border cursor-pointer transition-all ${
                        formData.vehicleType === v.type ? 'border-desti-gold bg-desti-gold/5' : 'border-white/10 hover:border-white/20'
                      }`}
                      data-testid={`vehicle-card-${v.type}`}>
                      <div className="flex items-center gap-4">
                        <img src={v.imageUrl} alt={v.name} className="w-24 h-16 object-cover" />
                        <div className="flex-1">
                          <h4 className="text-white font-medium">{v.name}</h4>
                          <p className="text-xs text-desti-text mt-1">{v.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-white/50">
                            <span className="flex items-center gap-1"><Users size={14} /> {v.seats} places</span>
                            <span className="text-desti-gold font-medium">{v.pricePerKm?.toFixed(2)} €/km</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {vehicles?.length === 0 && <p className="text-white/40 text-center py-4">Aucun véhicule disponible pour le moment.</p>}
                </div>
                {priceInfo && (
                  <div className="bg-desti-black/50 p-4 border border-desti-gold/20 space-y-2" data-testid="price-info">
                    <div className="line-gold my-2" />
                    <div className="flex justify-between text-lg"><span className="text-desti-gold font-heading">Total TTC</span><span className="text-white font-medium">{priceInfo.priceTTC?.toFixed(2)} €</span></div>
                  </div>
                )}
                <div className="flex gap-3">
                  <button onClick={() => handleStepChange(1)} className="px-6 py-3 border border-white/20 text-white text-sm hover:bg-white/5 transition-colors" data-testid="step2-back-btn">
                    <CaretLeft size={16} weight="bold" />
                  </button>
                  <button onClick={() => handleStepChange(3)} disabled={!formData.vehicleType}
                    className="flex-1 bg-desti-gold text-desti-black font-medium py-3 tracking-wide hover:bg-desti-gold-hover transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="step2-next-btn">
                    Paiement <CaretRight size={16} weight="bold" />
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <h3 className="font-heading text-xl text-white mb-6">Paiement</h3>
                {!user && (
                  <div className="space-y-4 mb-6">
                    <p className="text-xs uppercase tracking-widest text-desti-gold">Vos coordonnées</p>
                    <input type="text" placeholder="Nom complet" value={formData.contactName}
                      onChange={e => setFormData(p => ({ ...p, contactName: e.target.value }))}
                      className="w-full bg-desti-black border-b border-white/20 px-3 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-desti-gold"
                      data-testid="contact-name-input" />
                    <input type="email" placeholder="Email" value={formData.contactEmail}
                      onChange={e => setFormData(p => ({ ...p, contactEmail: e.target.value }))}
                      className="w-full bg-desti-black border-b border-white/20 px-3 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-desti-gold"
                      data-testid="contact-email-input" />
                    <input type="tel" placeholder="Téléphone" value={formData.contactPhone}
                      onChange={e => setFormData(p => ({ ...p, contactPhone: e.target.value }))}
                      className="w-full bg-desti-black border-b border-white/20 px-3 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-desti-gold"
                      data-testid="contact-phone-input" />
                  </div>
                )}
                <div className="bg-desti-black/50 p-5 border border-white/10 space-y-3" data-testid="order-summary">
                  <p className="text-xs uppercase tracking-widest text-desti-gold mb-3">Récapitulatif</p>
                  <div className="flex justify-between text-sm"><span className="text-white/60">Départ</span><span className="text-white text-right max-w-[60%]">{formData.pickupAddress}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-white/60">Destination</span><span className="text-white text-right max-w-[60%]">{formData.dropoffAddress}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-white/60">Date</span><span className="text-white">{formData.pickupDate} à {formData.pickupTime}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-white/60">Véhicule</span><span className="text-white">{priceInfo?.vehicleName}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-white/60">Distance</span><span className="text-white">{formData.distanceKm} km</span></div>
                  <div className="line-gold my-3" />
                  <div className="flex justify-between text-lg"><span className="text-desti-gold font-heading">Total TTC</span><span className="text-white font-medium">{priceInfo?.priceTTC?.toFixed(2)} €</span></div>
                </div>
                <p className="text-xs text-white/40 text-center">Paiement sécurisé par Stripe. Vous serez redirigé vers la page de paiement.</p>
                <div className="flex gap-3">
                  <button onClick={() => handleStepChange(2)} className="px-6 py-3 border border-white/20 text-white text-sm hover:bg-white/5 transition-colors" data-testid="step3-back-btn">
                    <CaretLeft size={16} weight="bold" />
                  </button>
                  <button onClick={handlePayment} disabled={loading || (!user && (!formData.contactName || !formData.contactEmail))}
                    className="flex-1 bg-desti-gold text-desti-black font-medium py-3 tracking-wide hover:bg-desti-gold-hover transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="pay-btn">
                    {loading ? 'Redirection...' : 'Payer maintenant'}
                    <CreditCard size={16} weight="bold" />
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="hidden lg:block">
            <div ref={mapRef} className="w-full h-[500px] bg-desti-surface border border-white/5" data-testid="booking-map" />
          </div>
        </div>
      </div>
    </section>
  );
}

function ReviewsCarousel() {
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);

  useEffect(() => {
    api.get('/reviews?limit=5&page=1')
      .then(r => {
        setReviews(r.data.reviews || []);
        setAvgRating(r.data.avgRating || 0);
      })
      .catch(() => setReviews([]));
  }, []);

  if (!reviews || reviews.length === 0) return null;

  return (
    <section className="py-24 md:py-32 bg-desti-surface/50" data-testid="reviews-carousel-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-desti-gold mb-3">Témoignages</p>
          <h2 className="font-heading text-3xl sm:text-4xl text-white">Derniers avis clients</h2>
          <div className="flex items-center justify-center gap-2 mt-4">
            {[1, 2, 3, 4, 5].map(i => (
              <Star key={i} size={18} weight="fill" className={i <= Math.round(avgRating) ? 'text-desti-gold' : 'text-white/20'} />
            ))}
            <span className="text-sm text-white/60 ml-2">{avgRating}/5</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reviews.slice(0, 3).map((r, i) => (
            <motion.div key={r.id || i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-desti-surface border border-white/5 p-6 hover:-translate-y-1 transition-transform duration-300"
              data-testid={`review-card-${i}`}>
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map(s => <Star key={s} size={14} weight="fill" className={s <= r.rating ? 'text-desti-gold' : 'text-white/20'} />)}
              </div>
              <p className="text-sm text-white/70 leading-relaxed line-clamp-4">{r.comment}</p>
              <div className="mt-4 pt-4 border-t border-white/5">
                <p className="text-sm text-white">{r.userName || 'Client'}</p>
                <p className="text-xs text-white/30 mt-1">{r.createdAt ? new Date(r.createdAt).toLocaleDateString('fr-FR') : ''}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AdvantagesSection() {
  const advantages = [
    { icon: Car, title: 'Flotte Premium', desc: 'Véhicules haut de gamme, entretenus et climatisés.' },
    { icon: Shield, title: 'Chauffeurs Pro', desc: 'Chauffeurs expérimentés, discrets et professionnels.' },
    { icon: Clock, title: 'Disponible 24/7', desc: 'Service de réservation disponible 24 heures sur 24.' },
    { icon: CreditCard, title: 'Paiement Simple', desc: 'Paiement sécurisé en ligne par carte bancaire.' },
  ];

  const handleAdvantageClick = (title) => {
    analytics.trackEvent('advantage_click', { advantage: title });
  };

  return (
    <section className="py-24 md:py-32" data-testid="advantages-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-desti-gold mb-3">Pourquoi nous choisir</p>
          <h2 className="font-heading text-3xl sm:text-4xl text-white">L'excellence à votre service</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {advantages.map((a, i) => (
            <motion.div 
              key={a.title} 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }} 
              transition={{ delay: i * 0.1 }}
              onClick={() => handleAdvantageClick(a.title)}
              className="bg-desti-surface border border-white/5 p-6 text-center hover:-translate-y-1 transition-transform duration-300 cursor-pointer"
              data-testid={`advantage-${i}`}>
              <a.icon size={36} weight="duotone" className="text-desti-gold mx-auto mb-4" />
              <h3 className="font-heading text-lg text-white mb-2">{a.title}</h3>
              <p className="text-sm text-desti-text">{a.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <HeroSection />
      <BookingStepper />
      <ReviewsCarousel />
      <AdvantagesSection />
    </div>
  );
}